import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Loader2, Plus, Trash2, ArrowRight, Check,
} from 'lucide-react';
import { apiClient, TrialUsage } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { PageLoader } from './PageLoader';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

// Per-product free-trial allowance shown under "No credit card required".
// Workflows copy varies by trial tier: personal-email accounts get the reduced
// allowance (25/day, $50 credit); company accounts get the standard one.
const WORKFLOWS_TRIAL_PERK_STANDARD = '100 workflow executions / day ≈ $1000 in usage credit';
const WORKFLOWS_TRIAL_PERK_PERSONAL = '25 workflow executions / day ≈ $50 in usage credit';
const TRIAL_PERKS: Record<string, string> = {
  workflows: WORKFLOWS_TRIAL_PERK_STANDARD,
  incident_response: '1 K8s cluster + 1 cloud account',
  copilot: '10 messages / day',
};

// Per-product display name + cancellation consequence shown in the confirm modal.
const CANCEL_INFO: Record<string, { name: string; consequence: string }> = {
  workflows: {
    name: 'Workflows',
    consequence: 'Your workflows will stop running and you will lose access to the workflow builder. Usage already incurred this month will still be billed.',
  },
  incident_response: {
    name: 'Incident Response',
    consequence: 'Connected K8s clusters and cloud accounts will stop generating incidents, and you will lose access to incident detail and remediation.',
  },
  copilot: {
    name: 'Cloud AI Copilot',
    consequence: 'You will lose access to the Cloud AI Copilot chat. Messages already used this month will still be billed.',
  },
};

interface ProductSubscriptionsProps {
  /** Called once, after the first data load completes (lets the parent page gate on a single spinner). */
  onInitialLoad?: () => void;
  /** When true, the parent owns the initial loading spinner and this component renders nothing until loaded. */
  hideInitialLoader?: boolean;
}

interface ProductCardProps {
  name: string;
  product: string;
  plans: { plan: string; label: string; price: string; description: string }[];
  subscription: any;
  usage?: React.ReactNode;
  subscribingPlan: string | null;
  onSubscribe: (product: string, plan: string) => void;
  onCancel: (product: string) => void;
  onStartTrial: (product: string) => void;
  startingTrial: boolean;
  trialUsed: boolean;
  personalTrialTier?: boolean;
  /** Workflows trial consumption; shown as a tooltip on the trial badge. */
  trialUsage?: TrialUsage | null;
  /** Full-width layout: usage stats on the left, plan/actions on the right. */
  wide?: boolean;
}

function ProductCard({ name, product, plans, subscription, usage, subscribingPlan, onSubscribe, onCancel, onStartTrial, startingTrial, trialUsed, personalTrialTier, trialUsage, wide }: ProductCardProps) {
  const isActive = subscription && subscription.status === 'active';
  const isTrial = subscription && subscription.status === 'trial';
  const isExpiredTrial = isTrial && subscription.trial_ends_at && new Date(subscription.trial_ends_at) < new Date();
  const showSubscribe = !subscription || isExpiredTrial || (isTrial && !isExpiredTrial);
  const trialDaysLeft = isTrial && !isExpiredTrial && subscription.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(subscription.trial_ends_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  const trialPerk = product === 'workflows' && personalTrialTier
    ? WORKFLOWS_TRIAL_PERK_PERSONAL
    : TRIAL_PERKS[product];

  const trialBox = !subscription && !trialUsed && (
    <div className="flex items-center justify-between py-2 px-3 rounded border border-dashed text-sm gap-2">
      <div className="min-w-0">
        <div className="font-medium">14-day free trial</div>
        {trialPerk && <div className="text-xs text-muted-foreground">{trialPerk}</div>}
      </div>
      <Button size="sm" className="h-7 text-xs shrink-0" disabled={startingTrial} onClick={() => onStartTrial(product)}>
        {startingTrial ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Start Free Trial'}
      </Button>
    </div>
  );

  const planRows = plans.map(p => (
    <div key={p.plan} className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 py-2 px-3 rounded border text-sm">
      <div className="min-w-0">
        <div className="font-medium">{p.label}</div>
        <div className="text-xs text-muted-foreground">{p.description}</div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-mono">{p.price}</span>
        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!!subscribingPlan} onClick={() => onSubscribe(product, p.plan)}>
          {subscribingPlan === `${product}:${p.plan}` ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Subscribe'}
        </Button>
      </div>
    </div>
  ));

  const planSection = (
    <>
      {trialBox}

      {showSubscribe ? (
        <div className="space-y-2">
          {planRows}
          <div className="flex items-center justify-between py-2 px-3 rounded border text-sm">
            <div>
              <div className="font-medium">Enterprise</div>
              <div className="text-xs text-muted-foreground">Custom pricing, SLAs, dedicated support</div>
            </div>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => window.open('mailto:hello@usekestrel.ai?subject=Enterprise Plan Inquiry', '_blank')}>
              Talk to Sales <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      ) : isActive ? (
        <div className="flex items-center justify-between py-2 px-3 rounded border text-sm">
          <div>
            <div className="font-medium capitalize">{subscription.plan} plan</div>
            {subscription.units > 1 && <div className="text-xs text-muted-foreground">{subscription.units} units</div>}
          </div>
          <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground hover:text-red-600" onClick={() => onCancel(product)}>
            Cancel
          </Button>
        </div>
      ) : null}
    </>
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium">{name}</CardTitle>
          {isActive && <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 text-xs">Active</Badge>}
          {isTrial && !isExpiredTrial && (
            trialUsage ? (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="secondary" className="text-xs cursor-pointer">
                      Trial — {trialDaysLeft}d left
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="end" className="text-xs">
                    <div className="space-y-1">
                      <div className="flex items-center justify-between gap-6">
                        <span className="text-muted-foreground">Executions today</span>
                        <span className="font-mono">{trialUsage.executions_today} / {trialUsage.executions_per_day}</span>
                      </div>
                      <div className="flex items-center justify-between gap-6">
                        <span className="text-muted-foreground">Trial credit used</span>
                        <span className="font-mono">${trialUsage.credit_used.toFixed(2)} / ${trialUsage.credit_total.toFixed(0)}</span>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Badge variant="secondary" className="text-xs">Trial — {trialDaysLeft}d left</Badge>
            )
          )}
          {isExpiredTrial && <Badge variant="destructive" className="text-xs">Trial expired</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {wide && usage ? (
          isActive ? (
            // Full-width layout, subscribed: usage stat tiles fill the left
            // side. There's nothing to choose (workflows has a single
            // usage-based plan), so the right side is just Cancel — which
            // opens the existing confirmation modal.
            <div className="grid gap-6 lg:grid-cols-[1fr,auto] lg:items-center">
              <div>{usage}</div>
              <div className="flex lg:justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs text-muted-foreground hover:text-red-600"
                  onClick={() => onCancel(product)}
                >
                  Cancel subscription
                </Button>
              </div>
            </div>
          ) : (
            // Full-width layout, not subscribed: the plan box comes first,
            // then the stat tiles follow on the same row.
            <div className="flex flex-wrap items-center gap-x-10 gap-y-4">
              <div className="space-y-2">
                {trialBox}
                {plans.map(p => (
                  <div key={p.plan} className="flex flex-wrap items-center gap-x-6 gap-y-2 py-2 px-3 rounded border text-sm">
                    <div className="min-w-0">
                      <div className="font-medium">{p.label}</div>
                      <div className="text-xs text-muted-foreground">{p.description}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono">{p.price}</span>
                      <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!!subscribingPlan} onClick={() => onSubscribe(product, p.plan)}>
                        {subscribingPlan === `${product}:${p.plan}` ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Subscribe'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              {/* Let the stat tiles claim the remaining row width so they
                  spread out instead of bunching next to the plan box. */}
              <div className="flex-1 min-w-0">{usage}</div>
            </div>
          )
        ) : (
          <>
            {usage && <div className="text-xs text-muted-foreground">{usage}</div>}
            {planSection}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function ProductSubscriptions({ onInitialLoad, hideInitialLoader = false }: ProductSubscriptionsProps = {}) {
  const { user: authUser } = useAuth();
  // Legacy products (Incident Response, Cloud AI Copilot) are hidden unless
  // the tenant has been re-enabled via tenants.legacy_products_enabled.
  const legacyProductsEnabled = authUser?.legacy_products_enabled === true;
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [trialsUsed, setTrialsUsed] = useState<Record<string, boolean>>({});
  const [personalTrialTier, setPersonalTrialTier] = useState(false);
  const [trialUsage, setTrialUsage] = useState<TrialUsage | null>(null);
  const [usage, setUsage] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState<string | null>(null);
  const [checkoutPublishableKey, setCheckoutPublishableKey] = useState<string | null>(null);
  const [subscribingPlan, setSubscribingPlan] = useState<string | null>(null);
  const [newAlertProduct, setNewAlertProduct] = useState('workflows');
  const [newAlertType, setNewAlertType] = useState('cost');
  const [newAlertValue, setNewAlertValue] = useState('');
  const [newAlertSelectedEmails, setNewAlertSelectedEmails] = useState<string[]>([]);
  const [newAlertSelectedChannels, setNewAlertSelectedChannels] = useState<string[]>([]);
  const [newAlertSelectedDMs, setNewAlertSelectedDMs] = useState<string[]>([]);
  const [slackChannels, setSlackChannels] = useState<any[]>([]);
  const [slackUsers, setSlackUsers] = useState<any[]>([]);
  const [tenantUsers, setTenantUsers] = useState<any[]>([]);
  const [cancelProduct, setCancelProduct] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => { loadData(); }, []);

  const getSub = (product: string) => subscriptions.find((s: any) => s.product === product);

  // Usage alerts only make sense for products the tenant can actually use:
  // a live subscription (active, or a trial that hasn't expired). Only the
  // usage-metered products (workflows, copilot) are alertable at all.
  const isLiveSub = (s: any) =>
    s && (s.status === 'active' ||
      (s.status === 'trial' && (!s.trial_ends_at || new Date(s.trial_ends_at) >= new Date())));
  const alertableProducts = [
    { value: 'workflows', label: 'Workflows' },
    ...(legacyProductsEnabled ? [{ value: 'copilot', label: 'Cloud AI Copilot' }] : []),
  ].filter(p => isLiveSub(getSub(p.value)));

  // Reset threshold type when product changes
  useEffect(() => {
    setNewAlertType('cost');
  }, [newAlertProduct]);

  // Keep the selected alert product valid as subscriptions load/change:
  // never leave the dropdown pointing at a product the tenant can't use.
  useEffect(() => {
    if (!alertableProducts.some(p => p.value === newAlertProduct)) {
      setNewAlertProduct(alertableProducts[0]?.value ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptions]);

  const initialLoadNotified = React.useRef(false);
  const [initialLoaded, setInitialLoaded] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [subsFull, usageData, alertsData, catalogData, usersData] = await Promise.all([
        apiClient.getBillingSubscriptionsFull(),
        apiClient.getBillingUsage(),
        apiClient.getBillingAlerts(),
        apiClient.getWorkflowCatalog().catch(() => ({ slack_channels: [], slack_users: [] })),
        apiClient.listTenantUsers().catch(() => []),
      ]);
      setSubscriptions(subsFull.subscriptions);
      setTrialsUsed(subsFull.trials_used || {});
      setPersonalTrialTier(!!subsFull.personal_trial_tier);
      setTrialUsage(subsFull.trial_usage || null);
      setUsage(usageData);
      setAlerts(alertsData);
      setSlackChannels(catalogData.slack_channels || []);
      setSlackUsers(catalogData.slack_users || []);
      setTenantUsers(Array.isArray(usersData) ? usersData : usersData?.users || []);
    } catch { /* silent */ } finally {
      setLoading(false);
      if (!initialLoadNotified.current) {
        initialLoadNotified.current = true;
        setInitialLoaded(true);
        onInitialLoad?.();
      }
    }
  };

  const handleSubscribe = async (product: string, plan: string) => {
    try {
      setSubscribingPlan(`${product}:${plan}`);
      const response = await apiClient.productCheckout({ product, plan, units: 1 });
      if (response.client_secret && response.publishable_key) {
        setCheckoutClientSecret(response.client_secret);
        setCheckoutPublishableKey(response.publishable_key);
      }
    } catch (err: any) {
      console.error('Failed to create checkout:', err);
    } finally {
      setSubscribingPlan(null);
    }
  };

  const handleCancel = (product: string) => {
    setCancelProduct(product);
  };

  const handleCancelConfirm = async () => {
    if (!cancelProduct) return;
    try {
      setCancelling(true);
      await apiClient.cancelBillingSubscription(cancelProduct);
      await loadData();
      window.dispatchEvent(new Event('subscription-changed'));
    } catch { /* silent */ } finally {
      setCancelling(false);
      setCancelProduct(null);
    }
  };

  const [creatingAlert, setCreatingAlert] = useState(false);
  const [deletingAlertIds, setDeletingAlertIds] = useState<Set<string>>(new Set());

  const handleCreateAlert = async () => {
    if (!newAlertProduct || !newAlertValue || creatingAlert) return;
    const totalSelected = newAlertSelectedEmails.length + newAlertSelectedChannels.length + newAlertSelectedDMs.length;
    if (totalSelected === 0) return;
    setCreatingAlert(true);
    try {
      await apiClient.createBillingAlert({
        product: newAlertProduct,
        threshold_type: newAlertType,
        threshold_value: parseFloat(newAlertValue),
        notify_email: newAlertSelectedEmails.length > 0,
        notify_email_address: newAlertSelectedEmails.join(', '),
        notify_slack: newAlertSelectedChannels.length > 0 || newAlertSelectedDMs.length > 0,
        slack_channel_id: newAlertSelectedChannels[0] || undefined,
        slack_dm_user_id: newAlertSelectedDMs[0] || undefined,
      });
      setNewAlertValue('');
      setNewAlertSelectedEmails([]);
      setNewAlertSelectedChannels([]);
      setNewAlertSelectedDMs([]);
      await loadData();
    } catch {
      toast.error('Failed to create usage alert');
    } finally {
      setCreatingAlert(false);
    }
  };

  const handleDeleteAlert = async (id: string) => {
    if (deletingAlertIds.has(id)) return;
    setDeletingAlertIds(prev => new Set(prev).add(id));
    try {
      await apiClient.deleteBillingAlert(id);
      await loadData();
    } catch {
      toast.error('Failed to delete usage alert');
    } finally {
      setDeletingAlertIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const [startingTrialProduct, setStartingTrialProduct] = useState<string | null>(null);

  const handleStartTrial = async (product: string) => {
    try {
      setStartingTrialProduct(product);
      await apiClient.startProductTrial(product);
      await loadData();
      window.dispatchEvent(new Event('subscription-changed'));
    } catch {
      // On failure (e.g. 409 trial already used), refresh so the UI reflects
      // the true state and the trial offer is hidden.
      await loadData();
    } finally {
      setStartingTrialProduct(null);
    }
  };

  // Only gate on the initial load; refetches (e.g. after cancel/trial start)
  // keep the existing content on screen instead of flashing another spinner.
  if (loading && !initialLoaded) {
    if (hideInitialLoader) return null;
    return <PageLoader />;
  }

  const wfUsage = usage?.workflow_usage;
  const chatUsage = usage?.copilot_usage;

  // Threshold type options filtered by product
  const thresholdTypes = newAlertProduct === 'workflows'
    ? [{ value: 'cost', label: 'Cost ($)' }, { value: 'executions', label: 'Executions' }]
    : [{ value: 'cost', label: 'Cost ($)' }, { value: 'chats', label: 'Messages' }];

  return (
    <div className="space-y-4">
      {/* Stripe Checkout Modal */}
      <Dialog open={!!checkoutClientSecret} onOpenChange={(open) => { if (!open) { setCheckoutClientSecret(null); setCheckoutPublishableKey(null); loadData(); window.dispatchEvent(new Event('subscription-changed')); } }}>
        <DialogContent className="sm:max-w-lg p-0 gap-0">
          <DialogHeader className="px-6 pt-5 pb-3">
            <DialogTitle className="text-sm font-medium">Complete Payment</DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto">
            {checkoutClientSecret && checkoutPublishableKey && (
              <EmbeddedCheckoutProvider
                stripe={loadStripe(checkoutPublishableKey)}
                options={{ clientSecret: checkoutClientSecret }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <div className={legacyProductsEnabled ? 'grid grid-cols-1 min-[1240px]:grid-cols-3 gap-4' : 'grid grid-cols-1 gap-4'}>
        <ProductCard
          name="Workflows"
          product="workflows"
          plans={[
            { plan: 'usage', label: 'Usage-Based', price: '$0.10-$0.40/block', description: 'Pay per workflow block execution' },
          ]}
          subscription={getSub('workflows')}
          subscribingPlan={subscribingPlan}
          wide={!legacyProductsEnabled}
          usage={wfUsage ? (
            legacyProductsEnabled ? (
              <div className="space-y-1">
                <div className="flex justify-between"><span>Executions this month</span><span className="font-mono">{wfUsage.total_executions}</span></div>
                <div className="flex justify-between"><span>Cost this month</span><span className="font-mono">${(wfUsage.total_cost || 0).toFixed(2)}</span></div>
                <div className="flex gap-3 text-[10px]">
                  <span>Light: {wfUsage.light_blocks}</span>
                  <span>Regular: {wfUsage.regular_blocks}</span>
                  <span>Heavy: {wfUsage.heavy_blocks}</span>
                </div>
              </div>
            ) : (
              // Wide layout: usage as stat tiles so the full-width card
              // doesn't read as empty.
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-x-8 gap-y-4">
                {([
                  ['Executions this month', wfUsage.total_executions],
                  ['Cost this month', `$${(wfUsage.total_cost || 0).toFixed(2)}`],
                  ['Light blocks', wfUsage.light_blocks],
                  ['Regular blocks', wfUsage.regular_blocks],
                  ['Heavy blocks', wfUsage.heavy_blocks],
                ] as [string, React.ReactNode][]).map(([label, value]) => (
                  <div key={label}>
                    <div className="text-xs text-muted-foreground">{label}</div>
                    <div className="text-xl font-semibold font-mono mt-1">{value}</div>
                  </div>
                ))}
              </div>
            )
          ) : null}
          onSubscribe={handleSubscribe}
          onCancel={handleCancel}
          onStartTrial={handleStartTrial}
          startingTrial={startingTrialProduct === 'workflows'}
          trialUsed={!!trialsUsed['workflows']}
          personalTrialTier={personalTrialTier}
          trialUsage={trialUsage}
        />

        {legacyProductsEnabled && (
        <ProductCard
          name="Incident Response"
          product="incident_response"
          plans={[
            { plan: 'starter', label: 'Starter', price: '$200/mo', description: '1 K8s cluster + 1 cloud account' },
            { plan: 'growth', label: 'Growth', price: '$300/mo/unit', description: 'Up to 5 units' },
          ]}
          subscription={getSub('incident_response')}
          subscribingPlan={subscribingPlan}
          onSubscribe={handleSubscribe}
          onCancel={handleCancel}
          onStartTrial={handleStartTrial}
          startingTrial={startingTrialProduct === 'incident_response'}
          trialUsed={!!trialsUsed['incident_response']}
        />
        )}

        {legacyProductsEnabled && (
        <ProductCard
          name="Cloud AI Copilot"
          product="copilot"
          plans={[
            { plan: 'starter', label: 'Starter', price: '$100/mo', description: 'Up to 150 messages/mo, $1 overage' },
            { plan: 'growth', label: 'Growth', price: '$200/mo', description: 'Up to 500 messages/mo, $1 overage' },
          ]}
          subscription={getSub('copilot')}
          subscribingPlan={subscribingPlan}
          usage={chatUsage ? (
            <div className="flex justify-between">
              <span>Messages this month</span>
              <span className="font-mono">{chatUsage.total_chats}</span>
            </div>
          ) : null}
          onSubscribe={handleSubscribe}
          onCancel={handleCancel}
          onStartTrial={handleStartTrial}
          startingTrial={startingTrialProduct === 'copilot'}
          trialUsed={!!trialsUsed['copilot']}
        />
        )}
      </div>

      {/* Usage Alerts */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium">Usage Alerts</CardTitle>
        </CardHeader>
        <CardContent>
          {alertableProducts.length === 0 ? (
            <div className="text-xs text-muted-foreground mb-4">
              Start a trial or subscribe to a product to configure usage alerts.
            </div>
          ) : (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Select value={newAlertProduct} onValueChange={setNewAlertProduct}>
              <SelectTrigger className="h-8 w-[130px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {alertableProducts.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={newAlertType} onValueChange={setNewAlertType}>
              <SelectTrigger className="h-8 w-[110px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {thresholdTypes.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input type="number" placeholder="Threshold" value={newAlertValue}
              onChange={e => setNewAlertValue(e.target.value)} className="h-8 text-xs w-[110px]" />
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1 px-3">
                  {(() => {
                    const total = newAlertSelectedEmails.length + newAlertSelectedChannels.length + newAlertSelectedDMs.length;
                    return total === 0 ? 'Select recipients' : `${total} selected`;
                  })()}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[260px] p-0" align="start">
                <div className="max-h-[300px] overflow-y-auto">
                  {tenantUsers.length > 0 && (
                    <div className="p-2">
                      <div className="text-xs font-medium text-muted-foreground px-1 pb-1">Email</div>
                      {tenantUsers.map((u: any) => {
                        const email = u.email || u.Email;
                        if (!email) return null;
                        const selected = newAlertSelectedEmails.includes(email);
                        return (
                          <button
                            key={email}
                            className={`flex items-center gap-2 w-full text-left px-2 py-1.5 text-xs rounded hover:bg-accent ${selected ? 'bg-accent' : ''}`}
                            onClick={() => setNewAlertSelectedEmails(prev =>
                              selected ? prev.filter(e => e !== email) : [...prev, email]
                            )}
                          >
                            <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${selected ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                              {selected && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                            </div>
                            {email}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {slackChannels.length > 0 && (
                    <div className={`p-2 ${tenantUsers.length > 0 ? 'border-t' : ''}`}>
                      <div className="text-xs font-medium text-muted-foreground px-1 pb-1">Channels</div>
                      {slackChannels.map((ch: any) => {
                        const selected = newAlertSelectedChannels.includes(ch.id);
                        return (
                          <button
                            key={ch.id}
                            className={`flex items-center gap-2 w-full text-left px-2 py-1.5 text-xs rounded hover:bg-accent ${selected ? 'bg-accent' : ''}`}
                            onClick={() => setNewAlertSelectedChannels(prev =>
                              selected ? prev.filter(c => c !== ch.id) : [...prev, ch.id]
                            )}
                          >
                            <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${selected ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                              {selected && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                            </div>
                            #{ch.name || ch.id}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {slackUsers.length > 0 && (
                    <div className="p-2 border-t">
                      <div className="text-xs font-medium text-muted-foreground px-1 pb-1">DM Users</div>
                      {slackUsers.map((u: any) => {
                        const selected = newAlertSelectedDMs.includes(u.id);
                        return (
                          <button
                            key={u.id}
                            className={`flex items-center gap-2 w-full text-left px-2 py-1.5 text-xs rounded hover:bg-accent ${selected ? 'bg-accent' : ''}`}
                            onClick={() => setNewAlertSelectedDMs(prev =>
                              selected ? prev.filter(d => d !== u.id) : [...prev, u.id]
                            )}
                          >
                            <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${selected ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                              {selected && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                            </div>
                            {u.real_name || u.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
            <Button size="sm" className="h-8 text-xs gap-1" onClick={handleCreateAlert} disabled={creatingAlert}>
              {creatingAlert ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} {creatingAlert ? 'Adding...' : 'Add Alert'}
            </Button>
          </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>When</TableHead>
                <TableHead>Notify</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-[60px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {alerts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-6 text-muted-foreground text-xs">
                    No usage alerts configured.
                  </TableCell>
                </TableRow>
              ) : alerts.map((a: any) => {
                const productLabel = a.product === 'copilot' ? 'Cloud AI Copilot' : a.product === 'workflows' ? 'Workflows' : a.product;
                const thresholdLabel = a.threshold_type === 'cost'
                  ? `Cost ≥ $${a.threshold_value}`
                  : `${a.threshold_type.charAt(0).toUpperCase() + a.threshold_type.slice(1)} ≥ ${a.threshold_value}`;
                const notifyParts: string[] = [];
                if (a.notify_email) {
                  notifyParts.push(a.notify_email_address || 'Email');
                }
                if (a.notify_slack) {
                  if (a.slack_channel_id) notifyParts.push(`#${slackChannels.find((c: any) => c.id === a.slack_channel_id)?.name || a.slack_channel_id}`);
                  if (a.slack_dm_user_id) notifyParts.push(`DM ${slackUsers.find((u: any) => u.id === a.slack_dm_user_id)?.real_name || a.slack_dm_user_id}`);
                  if (!a.slack_channel_id && !a.slack_dm_user_id) notifyParts.push('Slack');
                }
                const notifyLabel = notifyParts.join(', ') || 'None';
                return (
                  <TableRow key={a.id}>
                    <TableCell className="text-xs">{productLabel}</TableCell>
                    <TableCell className="text-xs font-mono">{thresholdLabel}</TableCell>
                    <TableCell className="text-xs">{notifyLabel}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" className="h-6 px-1 text-muted-foreground hover:text-red-600" onClick={() => handleDeleteAlert(a.id)} disabled={deletingAlertIds.has(a.id)}>
                        {deletingAlertIds.has(a.id) ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ConfirmationModal
        open={!!cancelProduct}
        onClose={() => { if (!cancelling) setCancelProduct(null); }}
        onConfirm={handleCancelConfirm}
        title={cancelProduct ? `Cancel ${CANCEL_INFO[cancelProduct]?.name ?? ''} subscription?` : 'Cancel subscription?'}
        description={cancelProduct ? (CANCEL_INFO[cancelProduct]?.consequence ?? 'This will cancel your subscription for this product.') : ''}
        confirmText="Cancel subscription"
        cancelText="Keep subscription"
        variant="destructive"
        loading={cancelling}
      />
    </div>
  );
}
